import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { ApicallOption } from "@core-services/apicall.service";
import { ConfirmModalService } from "@core-services/confirm-modal.service";
import { ProviderType } from "@helpers/types";
import { PayAccountService } from "@main-services/pay_account.service";
import { SocketService } from "@main-services/socket.service";
import { ErrorClass, Limit, PasswordValidationError, PayoutAccount, PayoutProvider, ValidationError } from "@models";
import { BpModalHasSave } from "@models/interfaces/bp-modal-has-save";
import { FiatSendWrapperData } from "@models/interfaces/fiat-send-wrapper-data";
import { PasswordAuth } from "@models/password_auth";
import { FiatTransaction } from "@models/transaction";
import { VerifyPayeeMode, VerifyPayeeResult } from "@models/verify-payee";
import { WalletsBalancesFacadeService } from "@modules/+new/core/wallets-balances/domain/+state/wallets-balances.facade.service";
import { WalletBalance } from "@modules/+new/core/wallets-balances/domain/entities/wallet-balance.model";
import { FiatPrecisionSymbolPipe } from "@modules/core/pipes";
import { NewProfileFacade } from "@modules/main/components/profile/new-profile/state/new-profile.facade";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { EMPTY, Observable } from "rxjs";
import { finalize, map, switchMap, take } from "rxjs/operators";
import { FiatTransactionSuccessComponent } from "../../../success-screens/fiat-transaction-success/fiat-transaction-success.component";
import { FiatWithdrawalModalComponent } from "../fiat-withdraw-modal.component";

@UntilDestroy()
@Component({
	selector: "fiat-send",
	templateUrl: "./fiat-send-modal.component.html",
	styleUrls: ["./fiat-send-modal.component.scss"],
	standalone: false
})
export class FiatSendModalComponent
	extends FiatWithdrawalModalComponent
	implements OnInit, AfterViewInit, BpModalHasSave
{
	@Input()
	public data: FiatSendWrapperData = null;

	@Output()
	public onAbort: EventEmitter<void> = new EventEmitter();

	@Output()
	public onSuccess: EventEmitter<void> = new EventEmitter();

	protected label: string = "send";

	public sendEnabled: boolean = true;
	public header_key = "modal_send_header_payments";
	public how_much_money_key: string = "how_much_money_send";
	public min_amount_withdrawal_key: string = "min_amount_send_AMOUNT";
	public showLoadingSpinner = this.verifyPayeeService.showLoadingSpinner;

	public showPassword: boolean = true; // By default, we always show the password when flow starts

	// We need to temporary store the selected account (when inserting a payment reference step right after account selection and before limits call)
	private storedSelectedAccount: PayoutAccount = null;

	protected readonly flowName: string = "Send Fiat";
	protected readonly pathPrepend: string = "/send/fiat";

	public constructor(
		protected socketService: SocketService,
		protected providerService: PayAccountService,
		protected confirmModalService: ConfirmModalService,
		protected readonly walletsBalancesFacade: WalletsBalancesFacadeService,
		protected readonly fiatPrecisionSymbolPipe: FiatPrecisionSymbolPipe,
		protected profileFacade: NewProfileFacade
	) {
		super(
			socketService,
			providerService,
			confirmModalService,
			walletsBalancesFacade,
			fiatPrecisionSymbolPipe,
			profileFacade
		);
	}

	public ngOnInit(): void {
		this.verifyPayeeService.verifyPayeeMode.set(VerifyPayeeMode.SEND);
		this.walletsBalancesFacade
			.getFiatWalletBalance$(this.data.transaction["preselected"].getId())
			.pipe(untilDestroyed(this))
			.subscribe((balance: WalletBalance) => (this.balance = balance));
		this.modalService.modalDismissed$
			.pipe(
				take(1),
				switchMap(() => {
					if (this.verifyPayeeService.verifyPayeeResponse()?.id) {
						return this.verifyPayeeService.updateAcceptanceStatus().pipe(take(1));
					}
					return EMPTY;
				})
			)
			.subscribe();
	}

	// Override
	public stepBack(n: number): void {
		if (this.isReferenceVisible) {
			this.setReferenceVisible(false);
			this.storedSelectedAccount = null;
			return;
		}
		if (n === 1) {
			this.onAbort.emit();
		} else {
			super.stepBack(n);
		}
	}

	// Override
	public setEdit(step: number, b = false): void {
		if (this.isReferenceVisible) {
			this.setReferenceVisible(false);
			this.storedSelectedAccount = null;
		}
		super.setEdit(step, b);
	}

	public onPasswordSuccess($event: PasswordAuth): void {
		if (this.isSecondStep) {
			// We need do a first call to get the providers OBS before continuing
			this.accountsForProviderObs(this.getPaymentProvider()).subscribe((list) => {
				if (list.length !== 1) {
					// If length=0 or has a stack > 1, then we auto-select the provider and skip 1 step
					this.autoSelectProvider();
				}
				this.setPasswordVisible(false);
				this.pageChangeSubject.next();
				this.cdr.markForCheck();
			});
		} else {
			this.setPasswordVisible(false);
			this.pageChangeSubject.next();
			this.cdr.markForCheck();
		}

		if (this.data.transaction.isStatus("pending")) {
			// Component was called from TX-List Confirm button
			this.setSmsPinVisible(true);
			this.cdr.markForCheck();
		}
	}

	// Override
	public accountsForProviderObs(p: PayoutProvider): Observable<PayoutAccount[]> {
		return this.providerService.getListForProvider(p, this.fiat);
	}

	// This keeps the place for the logic in Step 2.1 html template
	private autoSelectProvider(): void {
		super.clickedProvider(this.getPaymentProvider());
	}

	private getPaymentProvider(): PayoutProvider {
		const fiat = this.fiat;
		return this.masterdataService.payout_providers.find(
			(p: PayoutProvider) => p.isEnabled() && p.sendEnabled(true) && p.hasFiat(fiat)
		);
	}

	public logoUrl(): string {
		return "/assets/images/powered-by-payments.svg";
	}

	// Override
	public isAddAllowed(p: ProviderType): boolean {
		return true; // Send flow has unlimited number of payout accounts. User can create as many as he wants, there is no allowed max number
	}

	public onPayoutAccountPasswordError(): void {
		this.setPasswordVisible(true);
	}

	public onSavePaymentReference(): void {
		this.selectAccount(this.storedSelectedAccount, true);
	}

	// When the user selects/creates a Payments Payout Account, we need to Insert a New Step (Payment Reference) before getting to limit/amount screen. This is why we override the selectAccount
	// Without override, the selectAccount() goes directly to limits call and amount screen.

	// Override
	public selectAccount(account: PayoutAccount, created = false): void {
		this.selectedAccount.set(account);
		if (!this.storedSelectedAccount) {
			this.setReferenceVisible(true);
			this.storedSelectedAccount = account;
			this.pageChangeSubject.next();

			return; // Means selectAccount() was called before adding the Payment Reference. Wait for onSavePaymentReference()
		}

		this.editAccount = null;

		if (this.edit > 0 && !this.verifyPayeeService.isVopFlagEnabled()) {
			if (this.account != null && this.account.getId() === account.getId()) {
				this.summary = true;
				this.doTrapFocusTime();
				this.storedSelectedAccount = null;
				this.setReferenceVisible(false);
				this.pageChangeSubject.next();
				return;
			}
		}

		this.apicallService
			.post(
				"user/limits",
				new ApicallOption(Limit, {
					type: "withdrawal",
					payout_provider_id: account.getProviderId(),
					fiat_id: this.fiat.getId(),
					payout_account_id: account.getId()
				})
			)
			.subscribe(
				(a) => {
					this.data.transaction.setFee(0);
					this.data.transaction["amount"] = "";
					this.account = account;
					this.edit = 0;
					this.limit = a[0];
					this.doTrapFocusTime();
					this.storedSelectedAccount = null; // Reset, so the user will be able to edit again the reference
					this.setReferenceVisible(false); // Hide payment reference
					this.pageChangeSubject.next();
					this.cdr.markForCheck();
				},
				(error) => {
					if (error instanceof ErrorClass) {
						this.infoService.setError(error);
						if (error instanceof PasswordValidationError) {
							// Request again the password
							this.setPasswordVisible(true);
						}
						return;
					}

					console.error(error);
					this.cdr.markForCheck();
				}
			);
	}

	// On Summary Send call:
	// Override
	public save(): void {
		if (!this.summary) {
			return;
		}
		const shouldSendVerificationId =
			this.verifyPayeeService.isVopFlagEnabled() && Boolean(this.verifyPayeeService.verifyPayeeResponse()?.id);
		this.apicallService
			.post(
				"fiatwallet/" + this.wallet.getId() + "/send",
				new ApicallOption(FiatTransaction, {
					amount: "" + this.data.transaction.getAmount(),
					auth_code: this.data.transaction._auth_code,
					payout_account_id: this.data.transaction.getPayoutAccountId(),
					payment_reference: this.data.transaction.getPaymentReference(),
					...(shouldSendVerificationId && {
						verification_id: this.verifyPayeeService.verifyPayeeResponse().id
					})
				})
			)
			.pipe(map((res) => res[0]))
			.subscribe(
				(transaction: FiatTransaction) => {
					this.fiatwalletService.transactionReceived();
					this.fiatwalletService.initWallets(true);
					this.saved = true;
					this.account.wasUsed();

					// SMS PIN
					this.askForSmsPin(transaction);
					this.pageChangeSubject.next();
					this.cdr.markForCheck();
				},
				(error) => {
					if (error instanceof ErrorClass) {
						this.infoService.setError(error);

						if (error instanceof PasswordValidationError) {
							// Request again the password
							this.setPasswordVisible(true);
						} else if (error instanceof ValidationError) {
							this.data.transaction.setValidationErrors(error);
						} else {
							this.infoService.setError(error);
						}
					} else {
						console.error(error);
					}
					this.cdr.markForCheck();
				}
			);
	}

	// Show SMS PIN screen
	public askForSmsPin(transaction: FiatTransaction): void {
		this.data.transaction = transaction;
		this.setSmsPinVisible(true);
	}

	// Show Success Screen
	public onSmsPinSuccess(transaction: FiatTransaction): void {
		this.onSuccess.emit();
		this.modalService.getCurrentModal().dismiss();
		this.modalService
			.create(FiatTransactionSuccessComponent, { fiatTransaction: transaction }, { backButtonRoleDismiss: true })
			.present();
	}

	// PIN validation time expired
	public onSmsPinPasswordLocked(): void {
		this.setPasswordVisible(true);
	}

	// Steps

	public setPasswordVisible(value: boolean): void {
		this.showPassword = value;
	}

	public setReferenceVisible(value: boolean): void {
		this.showPaymentReference = value;
	}

	public setSmsPinVisible(value: boolean): void {
		this.showSmsPin = value;
	}

	protected moveToSummary(): void {
		super.moveToSummary();
		this.pageChangeSubject.next();
	}

	public initTracking(): void {
		this.pageChangeSubject
			.pipe(finalize(() => this.rudderstackFlowState(this.saved ? "successful" : "cancelled")))
			.subscribe(() => {
				if (this.summary) {
					if (this.isSmsPinVisible) {
						this.rudderstackPageFlow("PIN", "pin", {
							currency: this.fiat.getSymbol(true),
							send_amount: "" + this.amount
						});
					} else {
						this.rudderstackPageFlow("Summary", "summary", {
							currency: this.fiat.getSymbol(true),
							send_amount: "" + this.amount
						});
					}
				} else if (this.isThirdStep) {
					this.rudderstackPageFlow("Amount", "amount", {
						currency: this.fiat.getSymbol(true)
					});
				} else if (this.isSecondStep) {
					if (this.showPassword) {
						this.rudderstackPageFlow("Password Entry", null, {
							currency: this.fiat.getSymbol(true)
						});
					} else if (this.isReferenceVisible) {
						this.rudderstackPageFlow("Payment Reference", "reference", {
							currency: this.fiat.getSymbol(true)
						});
					} else {
						this.rudderstackPageFlow("Payout Account", "account", {
							currency: this.fiat.getSymbol(true)
						});
					}
				}
			});

		this.rudderstackFlowState("started");

		this.pageChangeSubject.next();
	}

	public getAccountProviders(): ProviderType[] {
		const items: ProviderType[] = [];
		const fiat = this.fiat;

		this.masterdataService.payout_providers
			.filter((p: PayoutProvider) => p.isEnabled())
			.filter((p: PayoutProvider) => p.sendEnabled(true))
			.filter((p: PayoutProvider) => p.hasFiat(fiat))
			.forEach((p: PayoutProvider) => {
				const accounts = this.accountsForProvider(p);
				if (accounts.length === 1) {
					items.push(accounts[0]);
				} else if (p.hasFiat(this.fiat)) {
					items.push(p);
				}
			});
		return items;
	}

	public getProvidersNotForFiat(): PayoutProvider[] {
		const fiat = this.fiat;
		return this.masterdataService.payout_providers
			.filter((p: PayoutProvider) => p.isEnabled())
			.filter((p: PayoutProvider) => p.sendEnabled(true))
			.filter((p: PayoutProvider) => !p.hasFiat(fiat))
			.filter((p: PayoutProvider) => p.getFees().length > 0);
	}

	public goToFiatWallet(): void {
		this.close();
		this.router.navigateByUrl(`/fiat-wallet/${this.wallet.getId()}`);
	}

	public verifyPayeeAction(): void {
		const result: VerifyPayeeResult = this.verifyPayeeService.verifyPayeeResponse().result;
		if (result === VerifyPayeeResult.ERROR) {
			this.goToSummary();
		}
		if (result === VerifyPayeeResult.NO_MATCH || result === VerifyPayeeResult.CLOSE_MATCH) {
			this.setEdit(2);
		}
	}
}
